import { useRuntimeConfig } from '#imports'
import { ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { getBrowserCookie } from '@backmarket/utils/cookies/getBrowserCookie'

import type { IntercomApiParameters } from '../../types/intercom'
import { isClient } from '../../utils/isClient'
import { useCareLogger } from '../logger/useCareLogger'

import translations from './useIntercomInvoker.translations'

export const useIntercomInvoker = () => {
  const { logIntercomError } = useCareLogger()
  const i18n = useI18n()
  const { openErrorToast } = useTheToast()

  const intercomMethodErrorAlreadyShown = ref(false)

  const callIntercomMethod = (...args: IntercomApiParameters) => {
    if (!isClient()) {
      logIntercomError({
        errorTitle: '[Intercom] - Trying to call intercom method during SSR',
        errorDetail: {
          intercomMethodEvent: args['0'],
          intercomMethodArgs: args['1'],
          environement: 'server',
        },
      })

      return
    }

    const intercom = window.Intercom

    if (typeof intercom === 'function') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      intercom(...(args as [any, ...any[]]))

      return
    }

    if (!intercomMethodErrorAlreadyShown.value) {
      intercomMethodErrorAlreadyShown.value = true

      logIntercomError({
        errorTitle:
          '[Intercom] - Trying to call a method when intercom is not available',
        errorDetail: {
          intercomMethodEvent: args['0'],
          intercomMethodArgs: args['1'],
          environement: isClient() ? 'browser' : 'server',
        },
      })
    }

    openErrorToast({
      title: i18n(translations.toastErrorTitle),
      content: i18n(translations.toastErrorContent),
    })
  }

  const deleteIntercomCookie = () => {
    const { market } = useMarketplace()
    const { INTERCOM_APP_ID } = useRuntimeConfig().public

    const intercomCookieName = `intercom-session-${INTERCOM_APP_ID}`

    const intercomCookie = getBrowserCookie(intercomCookieName)

    if (intercomCookie) {
      document.cookie = `${intercomCookieName}=; Max-Age=0; path=/; domain=${market.domain};`
    }
  }

  const resetIntercomMethodError = () => {
    intercomMethodErrorAlreadyShown.value = false
  }

  return {
    callIntercomMethod,
    deleteIntercomCookie,
    resetIntercomMethodError,
  }
}
